import {
  isFundingFilterId,
  isRateFilterId,
} from '../../../../shared/user-preferences/utils/currency-formatting';
import { type Filter, FilterId, FilterType } from '../types/filters';
import { getFormattedDateString } from '../utils/dateUtils';
import { isMaxEffectivelySet, isMinEffectivelySet } from './common';

type FormatFilterContext = {
  currencySymbol?: string;
  exchangeRate?: number;
  dateGranularity?: 'week' | 'month';
};

/**
 * A list of values as a chip prints it: up to three, comma separated, then
 * "+N more"; null for no values, which the chip takes as nothing to show.
 */
export const formatValueList = (values: string[]): string | null => {
  const count = values.length;
  if (count === 0) return null;
  if (count <= 1) return values[0] ?? null;
  if (count <= 3) return values.join(', ');
  const displayed = values.slice(0, 3).join(', ');
  return `${displayed} +${count - 3} more`;
};

const formatList = (values: string[]) => formatValueList(values) ?? '';

export function formatFilterDisplay(
  filter: Filter,
  ctx: FormatFilterContext = {}
): string {
  const { currencySymbol, exchangeRate = 1, dateGranularity = 'month' } = ctx;

  switch (filter.type) {
    case FilterType.DATE:
      if ('value' in filter && filter.value) {
        return getFormattedDateString(filter.value, dateGranularity);
      }
      return '';

    case FilterType.DATE_RANGE:
      if ('min' in filter && 'max' in filter && filter.min && filter.max) {
        const s = getFormattedDateString(filter.min, dateGranularity);
        const e = getFormattedDateString(filter.max, dateGranularity);
        return s && e ? `${s} to ${e}` : '';
      }
      return '';

    case FilterType.RANGE: {
      if (!('min' in filter) || !('max' in filter)) return '';
      const min = filter.min;
      const max = filter.max;
      const isPay = filter.id === FilterId.PAY;
      const fmt = (v: number) =>
        isPay && currencySymbol
          ? `${currencySymbol}${Math.round(v * exchangeRate).toLocaleString()}`
          : String(v);

      const hasMin = typeof min === 'number' && (!isPay || min > 0);
      const hasMax = typeof max === 'number';

      if (hasMin && hasMax) {
        if (min === max) return fmt(min);
        return `${fmt(min)} to ${fmt(max)}`;
      }
      if (hasMin) return `greater than ${fmt(min)}`;
      if (hasMax) return `less than ${fmt(max)}`;
      return '';
    }

    case FilterType.STATIC_LIST:
    case FilterType.LIST: {
      if (!('selections' in filter)) return '';
      return formatList(filter.selections.map((s) => s.name));
    }

    case FilterType.TREE: {
      const selections = filter.selections;
      if (selections.length > 0) {
        return formatList(selections.map((s) => s.name));
      }
      const primarySelections = filter.primarySelections;
      if (primarySelections?.length) {
        const activeNames = primarySelections.map((s) => s.name);
        if (activeNames.length > 0) {
          return formatList(activeNames);
        }
      }
      return '';
    }

    case FilterType.TEXT:
      if ('value' in filter && filter.value != null) {
        return String(filter.value);
      }
      return '';

    case FilterType.FREE_TEXT:
      if ('text' in filter) return formatList(filter.text);
      return '';

    case FilterType.SCHOOL:
      if ('schoolResultItems' in filter) {
        return formatList(
          Object.values(filter.schoolResultItems).map((s) => s.name)
        );
      }
      return '';

    case FilterType.VALUE: {
      const v = (filter as { value?: unknown }).value;
      if (v == null) return '';
      if (typeof v === 'string' || typeof v === 'number') return String(v);
      const obj = v as Record<string, unknown>;
      if (typeof obj.name === 'string') return obj.name;
      if (typeof obj.shortName === 'string') return obj.shortName;
      if (typeof obj.longName === 'string') return obj.longName;
      return '';
    }

    default:
      return '';
  }
}

const getListItemCount = (filter: Filter): number => {
  if ('selections' in filter && Array.isArray(filter.selections)) {
    return filter.selections.length;
  }

  if ('text' in filter && Array.isArray(filter.text)) {
    return filter.text.length;
  }

  return 0;
};

/**
 * The words between a chip's name and its value: " is ", " is one of ",
 * " is not ", " is from ". Shared by the filter chip and the saved-set
 * details, so both read the same.
 */
export const getFilterOperatorText = (filter: Filter): string => {
  const isNot = 'isNot' in filter && filter.isNot === true;
  const notSuffix = isNot ? ' not' : '';

  if (filter.id === FilterId.SNAPSHOT_DATE || filter.id === FilterId.PAY) {
    return ` is${notSuffix} `;
  }

  if (filter.type === FilterType.DATE_RANGE) {
    return ` is${notSuffix} from `;
  }

  if (filter.type === FilterType.RANGE) {
    const { min, max } = filter;

    const isRateFilter = isRateFilterId(filter.id);
    const filterIsFunding = isFundingFilterId(filter.id);

    const minEffectivelySet = isMinEffectivelySet(
      min,
      filter.id,
      false, // isPayFilter - PAY is not a RANGE type in this context
      filterIsFunding
    );
    const maxEffectivelySet = isMaxEffectivelySet(
      max,
      filter.id,
      minEffectivelySet
    );
    const isBetween = minEffectivelySet && maxEffectivelySet;

    // Less than case or graduation year: always use " is "
    if (filter.id === FilterId.GRADUATION_YEAR || (isBetween && min === max)) {
      return ` is${notSuffix} `;
    }

    // Less than case or greater than case: always use " is "
    if (
      (!minEffectivelySet && maxEffectivelySet) ||
      (minEffectivelySet && !maxEffectivelySet)
    ) {
      return ` is${notSuffix} `;
    }

    // Neither min nor max effectively set (default values): use " is "
    if (!minEffectivelySet && !maxEffectivelySet) {
      return ` is${notSuffix} `;
    }

    // Between case with different values: use " from " for rate filters, " is from " for others
    if (isRateFilter) {
      return ` from `;
    }
    return ` is${notSuffix} from `;
  }

  if (
    filter.type === FilterType.STATIC_LIST ||
    filter.type === FilterType.TREE ||
    filter.type === FilterType.LIST ||
    filter.type === FilterType.FREE_TEXT ||
    filter.type === FilterType.SCHOOL
  ) {
    const count = getListItemCount(filter);
    if (isNot) {
      return ' is not ';
    }
    return count > 1 ? ' is one of ' : ' is ';
  }

  return ` is${notSuffix} `;
};
